import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PostalCodeToken, CountryToken, CountryRegionToken } from '@sleed/bizcommerce-api';
import { postalCodesApi, countriesApi, countryRegionsApi } from '~/lib/api/client';
import { formToToken, type PostalCodeFormValues } from '~/lib/schemas/postal-code';

export const postalCodeKeys = {
	all: ['postal-codes'] as const,
	list: () => [...postalCodeKeys.all, 'list'] as const,
	countries: () => [...postalCodeKeys.all, 'countries'] as const,
	regions: () => [...postalCodeKeys.all, 'regions'] as const
};

export function usePostalCodesList() {
	return useQuery<PostalCodeToken[]>({
		queryKey: postalCodeKeys.list(),
		queryFn: async () => (await postalCodesApi.getAll()).data.items ?? []
	});
}

export function usePostalCodeCountries() {
	return useQuery<CountryToken[]>({
		queryKey: postalCodeKeys.countries(),
		queryFn: async () => (await countriesApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function usePostalCodeRegions() {
	return useQuery<CountryRegionToken[]>({
		queryKey: postalCodeKeys.regions(),
		queryFn: async () => (await countryRegionsApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function usePostalCodeMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: postalCodeKeys.list() });
	const create = useMutation({
		mutationFn: async (form: PostalCodeFormValues) => (await postalCodesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: PostalCodeFormValues) => (await postalCodesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await postalCodesApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
